import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import fetchWrapper from "../../api/fetchWrapper";
import { ENDPOINT } from "../../api/endpoint";
import { setRefData } from "../../store/slices/filterSlice";
import { useDispatch } from "react-redux";
import { buildCountryFilterOptions } from "../../constants/countryGroups";
import { aggregateFieldLabelOptions } from "../../constants/fieldLabelAggregations";

export const useGetRefData = (
  selectedRegions: string[] = [],
  refresh = false
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["refData"],
    queryFn: () => fetchWrapper(ENDPOINT.GET_REF_DATA, { method: "GET" }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (refresh) {
      queryClient.invalidateQueries({
        queryKey: ["refData"],
      });
    }
  }, [refresh, queryClient]);

  const refData = query.data;

  useEffect(() => {
    if (refData) {
      dispatch(setRefData(refData));
    }
  }, [refData, dispatch]);

  const distinctRegions = useMemo(() => {
    return Array.from(
      new Set(
        refData?.["ref_country"]
          ?.map((item: any) => item.ts_region)
          .filter((region: string | undefined) => region?.trim())
      )
    );
  }, [refData]);

  const countryNames = useMemo(() => {
    return Array.from(
      new Set(
        refData?.["ref_country"]
          ?.map((item: any) => item.country_name)
          .filter(Boolean)
      )
    );
  }, [refData]);

  const countryOptions = useMemo(
    () => buildCountryFilterOptions(refData?.["ref_country"]),
    [refData]
  );

  const affiliateNames = useMemo(() => {
    if (!selectedRegions?.length) return [];
    const allAffiliates = new Set<string>();
    selectedRegions.forEach((region: string) => {
      countryOptions.byRegion[region]?.forEach((a) => allAffiliates.add(a));
    });
    return Array.from(allAffiliates);
  }, [countryOptions, selectedRegions]);

  const { riskValues, riskLabels } = useMemo(() => {
    const values: string[] = [];
    const labels: Record<string, string> = {};
    refData?.["ref_risk_type"]?.forEach((item: any) => {
      if (item.risk_type_code && item.risk_type) {
        values.push(item.risk_type_code);
        labels[
          item.risk_type_code
        ] = `${item.risk_type_code} - ${item.risk_type}`;
      }
    });
    return { riskValues: values, riskLabels: labels };
  }, [refData]);

  const distinctEvaluations = useMemo(() => {
    return refData?.["ref_evaluation_type"]?.map(
      (item: any) => item.evaluation_type
    );
  }, [refData]);

  const distinctStatus = useMemo(() => {
    return refData?.["ref_status"]?.map(
      (item: any) => item.record_status
    );
  }, [refData]);

  const distinctProducts = useMemo(() => {
    return refData?.["ref_product"]?.map(
      (item: any) => item.product
    );
  }, [refData]);

  const distinctSampleId = useMemo(() => {
    return refData?.["sample_data"]
      ?.map((item: any) => item.sample_id)
      ?.sort((a: string, b: string) => b.localeCompare(a)); // Sort descending (newest first)
  }, [refData]);

  const distinctFieldNames = useMemo(
    () =>
      aggregateFieldLabelOptions(
        refData?.["ref_field_name"]
          ?.map((item: any) => item.field_name)
          .filter(Boolean)
      ),
    [refData]
  );

  const validAffiliates = useCallback(
    (affiliatesSelected: string[] = [], regionsSelected: string[] = []) => {
      const validAffiliates = new Set<string>();
      regionsSelected.forEach((region) => {
        countryOptions.byRegion[region]?.forEach((a) => validAffiliates.add(a));
      });

      return affiliatesSelected.filter((a) => validAffiliates.has(a));
    },
    [countryOptions]
  );

  return {
    ...query,
    options: {
      distinctRegions,
      countryNames,
      affiliateNames,
      risks: { riskValues, riskLabels },
      distinctEvaluations,
      distinctStatus,
      distinctProducts,
      distinctSampleId,
      distinctFieldNames,
      validAffiliates,
      countryGroupMembers: countryOptions.members,
    },
  };
};
